import type { CompanyArchetype } from '../types/company';
import type { ReportTemplate } from '../types/reportTemplate';
import { STOCK_DEFAULT_TEMPLATE } from './stockDefaultTemplate';

/**
 * Provenance marker for non-direct template resolutions.
 *
 * `'archetype-fallback-missing-linked'` — the report linked a templateId that
 * no longer resolves (deleted template / dangling link), so the ARCHETYPE SEED
 * was substituted instead of silently dropping all template chrome. Surfaces
 * (debug panels, print bundles, QA tooling) can read this to distinguish a
 * genuine template hit from the dangling-link fallback.
 */
export type TemplateResolutionProvenance = 'archetype-fallback-missing-linked';

/** ReportTemplate plus an optional resolution-provenance stamp. Purely
 *  additive — every consumer of ReportTemplate keeps working unchanged. */
export interface ResolvedReportTemplate extends ReportTemplate {
  templateResolution?: TemplateResolutionProvenance;
}

export interface ResolveActiveReportTemplateInput {
  reportTemplateId?: string | null;
  templates: ReportTemplate[];
  activeTemplateId?: string | null;
  /** From TemplateOverrideProvider — undefined when no provider is mounted. */
  templateOverride?: ReportTemplate | null;
  archetype?: CompanyArchetype | null;
}

/**
 * Resolve the template that drives cover layout, section order, and theme merge.
 *
 * GOLDEN LOCK (William 2026-06-29): Template Studio retired — this now resolves
 * to the ONE golden design (`STOCK_DEFAULT_TEMPLATE` = the `memo` preset +
 * MemoCover + editorial chrome) for every report, except an explicit non-null
 * `templateOverride` (the Studio sample-preview path; the print-data bundle now
 * passes the golden stock as its override). The legacy 5-step precedence
 * (linked id → archetype seed → session/org default → stock) is retired; see the
 * body for the rationale + where per-company branding flows.
 */
export function resolveActiveReportTemplate(
  input: ResolveActiveReportTemplateInput,
): ResolvedReportTemplate | null {
  // GOLDEN LOCK (William 2026-06-29): Template Studio is retired. Every report —
  // all 11 types, all 4 surfaces (view · PDF · DOCX · PPTX) — renders the ONE
  // golden design: STOCK_DEFAULT_TEMPLATE (the `memo` preset + MemoCover +
  // editorial chrome). Stored per-report templateIds, account/org defaults, and
  // archetype seeds NO LONGER select a different design — they all resolve to the
  // golden stock. Per-company BRANDING (companyName · logoUrl · primaryColor ·
  // footer copy) is NOT carried on the template; it layers in downstream via
  // resolveEffectiveTheme's brand-only store delta + resolveReportBranding, so
  // each tenant keeps its identity over the fixed golden look.
  //
  // A non-null `templateOverride` is still honoured — it is the Studio
  // sample-preview path, and the print-data bundle now passes the golden stock
  // as its override too (buildPrintDataBundle / buildPrintDataBundleClient), so
  // every REAL report (view, PDF, DOCX, PPTX) resolves to the golden stock.
  if (input.templateOverride) return input.templateOverride;
  return STOCK_DEFAULT_TEMPLATE;
}
